/**
 * What kind of work a vacancy is, and what kind of work a candidate is looking
 * for: one list, both sides.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 *
 * The sustainability areas say what a job is *about* — water, reporting,
 * climate. They do not say what the person does all day, and that is the
 * question a candidate actually filters on: an environmental engineer does not
 * want the inside-sales role at a climate start-up, however many areas the two
 * share. The weekly digest filtered on areas alone, every vacancy carries about
 * four of the seventeen, and a subscriber had six to fourteen of them — so one
 * shared area let almost anything through, and a candidate complained that we
 * kept sending jobs from the wrong branch (September 2026).
 *
 * `jobs.department` could not answer it either: it is free text, 33 spellings
 * across 75 published vacancies, and 27 of them say only "Sostenibilidad".
 *
 * ── The rule that keeps this list useful ─────────────────────────────
 *
 * **One value per vacancy.** A vacancy is filed under the single kind of work
 * the person hired would do most of the time. Tagging several is what made the
 * area filter useless, and it would do the same here. A candidate may pick as
 * many as they like; a vacancy gets one.
 *
 * Kind of work, never employer: a recruiter at a solar company is "Personas y
 * talento", and a salesperson at a consultancy is "Comercial y ventas".
 *
 * ── Who reads it ─────────────────────────────────────────────────────
 *
 * The site (questionnaire, profile, digest card, vacancy forms) through the
 * `@config` alias, the panel's collections, and the backend (digest filters,
 * the vacancy classifier). So it imports nothing — see
 * `tests/shared-config-imports.test.mjs`. The guidance the classifier gives the
 * model lives in the backend (`services/job-function.ts`), keyed by these ids
 * and held to them by a test; it is prompt text, and the site has no use for it.
 *
 * Stored as `jobs.job_function` (one id, CHECK-constrained in drizzle/0076),
 * `digest_subscriptions.job_functions` (ids) and
 * `talents.questionnaire.preferred_job_functions` (ids, plus `any`).
 */

export interface JobFunctionOption {
    id: string;
    /** The recruiter's words. What every screen and email prints. */
    label: string;
    /** A few words of example, for the questions that ask a person to choose. */
    hint?: string;
}

export const JOB_FUNCTIONS: readonly JobFunctionOption[] = [
    { id: "consultoria", label: "Consultoría y asesoría", hint: "asesorar a otras organizaciones" },
    { id: "sostenibilidad_empresa", label: "Sostenibilidad dentro de una empresa", hint: "estrategia, reporting, programas" },
    { id: "medio_ambiente_calidad_seguridad", label: "Medio ambiente, calidad y seguridad", hint: "operaciones, planta, campo" },
    { id: "ingenieria_tecnica", label: "Ingeniería y técnico", hint: "energía, instalaciones, proyectos" },
    { id: "proyectos_sociales", label: "Proyectos sociales y comunitarios" },
    { id: "investigacion_politicas", label: "Investigación, academia y políticas públicas" },
    { id: "finanzas_inversion", label: "Finanzas e inversión sostenible" },
    { id: "comercial_ventas", label: "Comercial y ventas" },
    { id: "comunicacion_marketing", label: "Comunicación y marketing" },
    { id: "personas_talento", label: "Personas y talento" }
];

export const JOB_FUNCTION_IDS: readonly string[] = JOB_FUNCTIONS.map(f => f.id);

export const JOB_FUNCTION_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
    JOB_FUNCTIONS.map(f => [f.id, f.label])
);

/**
 * The questionnaire's way of saying "any of them".
 *
 * Only ever in `questionnaire.preferred_job_functions`, the same way `any` is
 * only ever in `preferred_work_model`: it is an answer about the person, not a
 * kind of work a vacancy can be, so it never reaches a vacancy or a digest
 * filter — there, "any" is the empty array.
 */
export const ANY_JOB_FUNCTION = "any";

export function isJobFunction(value: unknown): value is string {
    return typeof value === "string" && JOB_FUNCTION_IDS.includes(value);
}

/** The label, or the id itself for one this list no longer has. */
export function jobFunctionLabel(id: string): string {
    return JOB_FUNCTION_LABELS[id] ?? id;
}

/** One vacancy's value: a known id, or null. Never guesses. */
export function normalizeJobFunction(value: unknown): string | null {
    const id = typeof value === "string" ? value.trim() : "";
    return isJobFunction(id) ? id : null;
}

/**
 * A person's answer as a filter: known ids, de-duplicated, in list order.
 *
 * `any` anywhere in the answer empties it — "cualquiera" is not a preference,
 * and filtering to the other ids they ticked would hide vacancies they said
 * they were open to. Unknown ids are dropped rather than kept: a filter value
 * no vacancy can carry matches nothing, silently, forever.
 */
export function jobFunctionFilter(value: unknown): string[] {
    const raw = Array.isArray(value) ? value : [];
    const picked = new Set<string>();
    for (const item of raw) {
        if (typeof item !== "string") continue;
        const id = item.trim();
        if (id === ANY_JOB_FUNCTION) return [];
        if (isJobFunction(id)) picked.add(id);
    }
    return JOB_FUNCTION_IDS.filter(id => picked.has(id));
}
